<template>
    <div>
        <div v-if="hasErrors" class="flags mt-xs text--orange">
            <svg><use xlink:href="#alert" /></svg>
            <div>
                <p v-if="maxInverterWatts" class="small semi">PV Array output is greater than the Inverter’s listed ‘Max DC Input’ as required by the CEC Design Guidelines. Confirm the configuration with your product manufacturer.</p>
                <p v-if="nominalOuput" class="small semi">Inverter Nominal AC Output is less than 75% of the connected PV Array and will not be eligible for STCs</p>
            </div>
            <!-- Doesn't render, only used for calc -->
        </div>
        <template v-if="mpptFlags" />
    </div>
</template>

<script>
import { calculateSystemSize } from '@/../utils';

export default {
    props: {
        system: {
            type: Object,
            required: true
        },

        buildingType: {
            type: String,
            default: 'Undefined'
        },

        temperature: {
            type: Object,
            default: () => ({
                min: 0,
                max: 0
            })
        },
    },

    computed: {
        type() {
            return this.system.inverter.details.inverterType;
        },

        hasErrors() {
            return this.type == 'string' && (this.maxInverterWatts || this.nominalOuput);
        },

        systemSize() {
            return calculateSystemSize([this.system]);
        },

        maxInverterWatts() {
            return this.systemSize > this.system.inverter.details.maxDCWatts;
        },

        nominalOuput() {
            return this.systemSize > this.system.inverter.details.nominalOutputPower / 0.75;
        },

        mpptFlags() {
            const flags = this.system.mppts.map((mppt) => {
                if (this.type !== 'string' || !mppt.panel) return [];

                const minArrayVoltage = this.minArrayVoltage(this.system.inverter, mppt.panel, mppt.series, this.temperature.max);
                const isc = this.isc(mppt.id, this.system.inverter, mppt.panel, mppt.parallel);
                const maxSystemVoltage = this.maxSystemVoltage(mppt.panel, mppt.series, this.temperature.min);
                const maxInverterVoltage = this.maxInverterVoltage(this.system.inverter, mppt.panel, mppt.series, this.temperature.min);
                const maxvFlagBuildingType = this.maxvFlagBuildingType(mppt.panel, mppt.series, this.temperature.min);
                const overcurrentProtection = this.overcurrentProtection(mppt.panel, mppt.parallel);

                return [
                    minArrayVoltage,
                    isc,
                    maxSystemVoltage,
                    maxInverterVoltage,
                    maxvFlagBuildingType,
                    overcurrentProtection
                ].filter(Boolean);

            });

            this.$emit('updated', flags);
            return flags;
        }
    },

    methods: {
        maxv(panel, series, minTemp) {
            const { details: { voc, vDeratingCelsius }} = panel;
            const pvMaxVoltage = Number(voc) + ((minTemp - 25)  * (Number(voc) * Number(vDeratingCelsius) / 100));
            return Math.round(pvMaxVoltage * series);
        },

        minArrayVoltage(inverter, panel, series, maxTemp) {
            const { details: { vmp, voc, vDeratingCelsius }} = panel;
            const { details: { minMPPVoltage }} = inverter;
            const minVoltage = Number(vmp) - (maxTemp * (Number(voc) * Number(vDeratingCelsius) / 100));
            return (minVoltage * series) < Number(minMPPVoltage) ?  "PV Array Minimum Voltage less than the Inverter’s listed ‘MPP Voltage Range – Lower’ as required by the CEC Design Guidelines. Note; the PV Array Minimum Voltage is not checked against Inverter Start-up Voltage. Confirm the configuration with your product manufacturer." : null;
        },

        isc(id, inverter, panel, parallel) {
            const { details: { maxShortCircuitCurrent }} = inverter;
            const mpptmaxShortCircuitCurrent = maxShortCircuitCurrent[id - 1];
            const { details: { panelIsc }} = panel;
            return (panelIsc * parallel) > mpptmaxShortCircuitCurrent ? "Isc is greater than the listed ‘Max Short Circuit Current’ of the MPPT as required by the CEC Design Guidelines. Confirm the configuration with your product manufacturer." : null;
        },

        maxSystemVoltage(panel, series, minTemp) {
            const { details: { maxSystemVoltage }} = panel;
            return this.maxv(panel, series, minTemp) > maxSystemVoltage ? "PV Array Maximum Voltage is greater than the PV Module’s listed ‘Max System Voltage’ as required by AS/NZS5033. Confirm the configuration with your product manufacturer." : null;
        },

        maxInverterVoltage(inverter, panel, series, minTemp) {
            const { details: { dcInputVoltageRangeUpper }} = inverter;
            return this.maxv(panel, series, minTemp) > dcInputVoltageRangeUpper ? "PV Array Maximum Voltage is greater than the Inverter’s listed ‘DC Input Voltage Range – Upper’ as required by the CEC Design Guidelines. Confirm the configuration with your product manufacturer." : null;
        },

        maxvFlagBuildingType(panel, series, minTemp) {
            const buildingTypeV = this.buildingType === 'Non-domestic' ? 1500 : 600;
            return this.maxv(panel, series, minTemp) > buildingTypeV ? `PV Array Maximum Voltage exceeds the ${this.buildingType} limit for voltage` : null;
        },

        overcurrentProtection(panel, parallel) {
            const { details: { panelIsc, seriesFuseRating }} = panel;
            return (parallel - 1) * panelIsc > seriesFuseRating ? "String overcurrent protection (string fusing) may be required" : null;
        }
    }
};
</script>

<style lang="scss" scoped>
    .flags {
        .small {
            padding-bottom: 10px;
        }
    }
</style>
